/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import invert from 'lodash.invert';
import {translate} from '@ion/core/src/locale/locale';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  Account,
  CTCEntityResponseWrapper,
  EntityStatus,
  GenericObject,
  Link,
  User
} from '@ion/core/src/types/entities';
import {toDateISOString} from '@ion/core/src/utils/DateUtil';
import {convertStatus} from '@ion/dls/src/utils/ComponentUtils';
import {importUsers} from '../../../store/actions/importer';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';

// defines the prop definitions for import start ...
export interface ImportStartType {
  items: User[] | GenericObject;
  account?: Account;
  application?: Link;
  context?: UserContext;
  dispatch?: Function;
}

/**
 * Import process starts after validated Excel
 *
 */
export function onImportStart({
  items,
  account,
  application,
  dispatch,
  context
}: ImportStartType) {
  const users = augmentItems(items, account, application);
  if (items?.length) {
    dispatch(importUsers(users, context));
  } else {
    dispatch(toggleUserPrompt({message: translate('admin-users:import.errors.emptyUsers')}));
  }
}

/**
 * @returns the units of measurement ...
 */
export function getUOM(): object {
  return translate('admin-users:uom', {returnObjects: true});
}

/**
 * @returns the role url from account ...
 */
export function getRoleURL(account: Account): string {
  let {href} = account;
  href = href.substr(0, href.indexOf('/accounts'));
  return `${href}/roles/search`;
};

/**
 * Augments the imported items with additional infered User attributes
 * required by CTC Services.
 *
 */
export const augmentItems = (
  items: User[] | GenericObject,
  account: Account,
  application: Link
): CTCEntityResponseWrapper<User>[] => {
  const users  = [];
  const uomObj = getUOM();
  items.forEach(item => {
    const url    = getRoleURL(account);
    const {role, id, status} = item;
    item.passwordUpdated = false;
    item.applications    = [application];
    item.status          = convertStatus(status);
    item.systemOfUnits   = {value: (invert(uomObj))[item.uom]};
    item.terminationDate = toDateISOString(item.terminationDate);
    if (role) {
      item.roles = [{
        href : `${url}/${role}`,
        rel  : 'role'
      }];
    } else {
      item.roles = [];
    }
    item.id ||= null;
    item.status ||= EntityStatus.Enabled;
    users.push({user: item});
  });
  return users;
};
